import { type CSSProperties } from 'react';

import { type Theme } from '@mui/material/styles';

import { type QuantitativeSizeString } from '../../types/sizes';
import { evaluateHtmlColor } from '../../util/colors';
import { getFullDimensions, type OppositeDimensionSizing } from '../../util/dimensions';
import { quantitativeSizeToString } from '../../util/sizes';

import * as types from './types';

export const getIconSize = (size: types.IconSize): QuantitativeSizeString => {
  if (types.isIconDiscreteSize(size)) {
    return types.IconDiscreteSizes[size];
  }
  return quantitativeSizeToString(size, 'px');
};

interface GetIconStyleParams extends types.IconStyleParams {
  /**
   * Defines whether or not the style, {@link CSSProperties}, for the {@link Icon} should be created
   * in the context of an existing element that is being cloned.  If this is true, the size
   * properties of the {@link Icon} will not be defaulted.
   *
   * @default false
   */
  readonly isCloning?: boolean;
  /**
   * Defines whether or not the style, {@link CSSProperties}, for the {@link Icon} should be created
   * in the context of the {@link Icon} being wrapped by the {@link IconContainer}.  If this is
   * true, the size dimension, {@link types.IconDimension}, will be defaulted if not present, and
   * the color will be set to 'inherit'.
   *
   * @default false
   */
  readonly isWrapped?: boolean;
  readonly oppositeAxisDimension?: Extract<OppositeDimensionSizing, 'auto' | 'square'>;
  readonly style?: React.CSSProperties;
}

/**
 * Represents CSS Properties for the {@link Icon} component that represent defaults, and are only
 * attributed to the {@link Icon} component if the {@link Icon} component is not cloning an existing
 * element or the cloned existing element does not have the given property in it's 'style'.
 */
const IconBaseStyle = {
  alignItems: 'center',
  aspectRatio: 1,
  boxSizing: 'border-box',
  display: 'inline-flex',
  flexDirection: 'column',
  justifyContent: 'center',
} as const satisfies CSSProperties;

const DefaultSizeConstraints: Record<
  types.IconSizeConstraintCssProperty,
  types.IconSizeConstraintCssValue | undefined
> = {
  maxHeight: '100%',
  maxWidth: '100%',
  minHeight: undefined,
  minWidth: undefined,
};

const getIconSizeConstraints = ({
  style,
  ...params
}: Pick<GetIconStyleParams, 'style' | types.IconSizeConstraintCssProperty>) =>
  types.IconSizeConstraintCSSProperties.reduce((acc: types.IconSizeConstraints, property) => {
    const providedValue = params[property]
      ? quantitativeSizeToString(params[property], 'px')
      : undefined;
    return {
      ...acc,
      [property]: style?.[property] ?? providedValue ?? DefaultSizeConstraints[property],
    };
  }, {});

export const getIconStyle = (
  theme: Theme,
  {
    color,
    dimension: _dimension,
    isCloning = false,
    isWrapped = false,
    oppositeAxisDimension = 'auto',
    size: _size,
    style = {},
    ...params
  }: GetIconStyleParams,
): Record<string, unknown> => {
  const dimension = _dimension ?? (isCloning ? undefined : types.DefaultIconDimension);

  const baseStyle = Object.keys(IconBaseStyle).reduce(
    (acc: CSSProperties, key) => ({
      ...acc,
      [key as keyof CSSProperties]:
        style[key as keyof CSSProperties] ?? IconBaseStyle[key as keyof typeof IconBaseStyle],
    }),
    {},
  );

  if (isWrapped) {
    return {
      ...style,
      ...baseStyle,
      ...getFullDimensions('100%', dimension ?? 'height', oppositeAxisDimension),
      ...getIconSizeConstraints({ style, ...params }),
      color: 'inherit',
    };
  }
  const size = _size ?? (isCloning ? undefined : types.DefaultIconSize);
  if (dimension && size) {
    return {
      ...style,
      ...baseStyle,
      ...getFullDimensions(getIconSize(size), dimension, oppositeAxisDimension),
      ...getIconSizeConstraints({ style, ...params }),
      color: color ? evaluateHtmlColor(theme, color) : style.color,
    };
  }
  return {
    ...style,
    ...baseStyle,
    ...getIconSizeConstraints({ style, ...params }),
    color: color ? evaluateHtmlColor(theme, color) : style.color,
  };
};
